/**
 * resolveBacklineImages.ts — resolve backline image URLs to data URLs so the
 * server-side pdfmake can embed them in the rider PDF.
 *
 * pdfmake under Node supports PNG/JPEG data URLs only. `/static/uploads/...`
 * paths are fetched with credentials, WebP/GIF/AVIF blobs are re-encoded to
 * PNG through a canvas, and PNG/JPEG data URLs pass through unchanged.
 *
 * Extracted from RiderPage (2026-08-10) so the Rider Editor preview resolves
 * backline images the same way — without this, the editor's docDef contains
 * relative `static/uploads/...webp` paths and the /api/pdf render 500s
 * ("File '...' not found in virtual file system").
 */

type WithBackline = { backline?: { imageUrl?: string }[] };

/** Re-encode an arbitrary image data URL (WebP/GIF/AVIF) to PNG. */
function reencodeToPng(dataUrl: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(undefined), 8000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 400;
        canvas.height = img.height || 400;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(undefined); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch { resolve(undefined); }
    };
    img.onerror = () => { clearTimeout(timer); resolve(undefined); };
    img.src = dataUrl;
  });
}

/** Résout UNE image (URL relative/absolue, data URL) en data URL PNG/JPEG
 *  embarquable par pdfmake. Exportée (2026-11-03) pour la photo de salle. */
export async function resolveImageToDataUrl(u: string | undefined): Promise<string | undefined> {
  return resolveImg(u);
}

async function resolveImg(u: string | undefined): Promise<string | undefined> {
  if (!u) return undefined;
  if (u.startsWith('data:')) {
    // pdfmake/Node supports PNG/JPEG data URLs only — re-encode the
    // others (WebP/GIF/AVIF) to PNG through a canvas.
    if (u.startsWith('data:image/png') || u.startsWith('data:image/jpeg')) return u;
    return await reencodeToPng(u);
  }
  try {
    const res = await fetch(u, { credentials: 'include' });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    if (blob.type === 'image/webp' || blob.type === 'image/gif' || blob.type === 'image/avif') {
      // Re-encode to PNG via canvas (pdfmake/Node cannot embed these).
      const url = URL.createObjectURL(blob);
      try {
        return await new Promise<string | undefined>((resolve) => {
          const img = new Image();
          const timer = setTimeout(() => { URL.revokeObjectURL(url); resolve(undefined); }, 8000);
          img.onload = () => {
            clearTimeout(timer);
            URL.revokeObjectURL(url);
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width || 400;
              canvas.height = img.height || 400;
              const ctx = canvas.getContext('2d');
              if (!ctx) { resolve(undefined); return; }
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL('image/png'));
            } catch { resolve(undefined); }
          };
          img.onerror = () => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(undefined); };
          img.src = url;
        });
      } catch {
        URL.revokeObjectURL(url);
        return undefined;
      }
    }
    return await new Promise<string | undefined>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

/**
 * Resolve every backline item's imageUrl to an embeddable data URL (or
 * undefined when the fetch/encode fails). Returns new objects; inputs untouched.
 */
export async function resolveBacklineImages<T extends WithBackline>(list: T[]): Promise<T[]> {
  return Promise.all(list.map(async (item) => ({
    ...item,
    backline: await Promise.all((item.backline || []).map(async (bl) => ({
      ...bl,
      imageUrl: await resolveImg(bl.imageUrl),
    }))),
  })));
}
